/**
 * Sankey der Zählerdaten: **Energiefluss** (Quelle → Unterzähler → … mit Rest je Knoten) oder **nach Mietern**
 * (Quelle → Mieteinheiten → Allgemein / nicht zugeordnet). Layout mit d3-sankey, gezeichnet als SVG; Breite
 * folgt dem Container.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { sankey, sankeyLinkHorizontal, type SankeyNode, type SankeyLink } from "d3-sankey";

export interface FlowData {
  nodes: Array<{ id: string; name: string; kind: "source" | "meter" | "rest" | "unit" | "common" }>;
  links: Array<{ source: string; target: string; value: number }>;
}

const PALETTE = ["#5F3A79", "#98AC4D", "#001A61", "#E07A2F", "#2A9D8F", "#B5838D", "#6D597A", "#E9C46A", "#264653", "#8AB17D"];
const KIND_COLOR: Record<string, string> = { source: "#3D2150", rest: "#B0B0B8", common: "#B0B0B8" };
const nf = (v: number) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(v);

type N = SankeyNode<FlowData["nodes"][number], FlowData["links"][number]>;
type L = SankeyLink<FlowData["nodes"][number], FlowData["links"][number]>;

export default function MeterSankey({ flow, unit }: { flow: FlowData; unit: string }) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  useEffect(() => {
    if (!box.current) return;
    const observer = new ResizeObserver(entries => setWidth(Math.max(320, Math.floor(entries[0].contentRect.width))));
    observer.observe(box.current);
    return () => observer.disconnect();
  }, []);
  const leaves = flow.nodes.length - new Set(flow.links.map(l => l.source)).size;
  const height = Math.max(240, Math.min(900, leaves * 26 + 40));

  const layout = useMemo(() => {
    if (!flow.links.length) return null;
    const generator = sankey<FlowData["nodes"][number], FlowData["links"][number]>()
      .nodeId(d => d.id)
      .nodeWidth(12)
      .nodePadding(8)
      .extent([
        [1, 6],
        [width - 1, height - 6],
      ]);
    try {
      return generator({ nodes: flow.nodes.map(n => ({ ...n })), links: flow.links.map(l => ({ ...l })) });
    } catch {
      return null;
    }
  }, [flow, width, height]);

  if (!layout) return <p className="text-sm text-muted-foreground">{t("meterData.noFlow")}</p>;
  const colorOf = (n: N) => KIND_COLOR[n.kind] ?? PALETTE[(n.index ?? 0) % PALETTE.length];
  const path = sankeyLinkHorizontal();

  return (
    <div ref={box} className="min-w-0" data-testid="meter-sankey">
      <svg width={width} height={height} role="img" aria-label={t("meterData.flow")}>
        <g fill="none">
          {(layout.links as L[]).map((l, i) => {
            const target = l.target as N;
            return (
              <path key={i} d={path(l) ?? undefined} stroke={colorOf(target)} strokeOpacity={0.3} strokeWidth={Math.max(1, l.width ?? 1)}>
                <title>{`${(l.source as N).name} → ${target.name}: ${nf(l.value)} ${unit}`}</title>
              </path>
            );
          })}
        </g>
        {(layout.nodes as N[]).map(n => {
          const left = (n.x0 ?? 0) < width / 2;
          const h = Math.max(1, (n.y1 ?? 0) - (n.y0 ?? 0));
          return (
            <g key={n.id}>
              <rect x={n.x0} y={n.y0} width={(n.x1 ?? 0) - (n.x0 ?? 0)} height={h} fill={colorOf(n)}>
                <title>{`${n.name}: ${nf(n.value ?? 0)} ${unit}`}</title>
              </rect>
              {h >= 9 ? (
                <text
                  x={left ? (n.x1 ?? 0) + 6 : (n.x0 ?? 0) - 6}
                  y={((n.y0 ?? 0) + (n.y1 ?? 0)) / 2}
                  dy="0.35em"
                  textAnchor={left ? "start" : "end"}
                  className="fill-foreground"
                  style={{ fontSize: 11 }}
                >
                  {`${n.name.length > 48 ? `${n.name.slice(0, 46)}…` : n.name} · ${nf(n.value ?? 0)}`}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
